import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Alert,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Warning as WarningIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Schedule as ScheduleIcon,
  Work as WorkIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationIcon,
  Build as BuildIcon,
  Star as StarIcon,
  SupervisorAccount as SupervisorIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useOrdenRealtime } from '../hooks/useOrdenRealtime';
import config from '../config';

// 🆕 IMPORTAR LOS NUEVOS COMPONENTES
import GestionTrabajadoresDialog from '../components/GestionTrabajadoresDialog';
import GestionServiciosDialog from '../components/GestionServiciosDialog';
import CamposDinamicosRenderer from '../components/CamposDinamicosRenderer';

// 🆕 COMPONENTES DE DIRECCIONES
import DireccionInput from '../components/forms/DireccionInput';
import DestinosMultiples from '../components/forms/DestinosMultiples';
import { useEmpresa } from '../context/EmpresaContext';

// ✅ ESTADOS CORREGIDOS - Usar números para consistencia
const estadosNoEditables = [5, 7, 8, 9, 10];
const estadosModificacionPermitida = [1, 2]; // Solo Pendiente(1) y Asignado(2)
const estadosAsignacionPermitida = [1, 2]; // Solo Pendiente(1) y Asignado(2)

const formatLocalDateTime = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Componente de Step personalizado
const CustomStepIcon = ({ active, completed, icon }) => {
  const icons = {
    1: <AssignmentIcon />,
    2: <WorkIcon />,
    3: <GroupIcon />
  };

  return (
    <Box
      sx={{
        width: 50,
        height: 50,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: completed 
          ? 'linear-gradient(135deg, #4CAF50 0%, #45A049 100%)'
          : active 
          ? 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)'
          : '#E0E0E0',
        color: completed || active ? 'white' : '#999',
        transition: 'all 0.3s ease'
      }}
    >
      {completed ? <CheckCircleIcon /> : icons[icon]}
    </Box>
  );
};

function OrdenServicioForm() {
  // ✅ ESTADO INICIAL CORREGIDO
  const [orden, setOrden] = useState({
    id_cliente: '',
    id_periodo: '',
    id_local: '',
    id_tiposervicio: 1,
    fecha_orden: formatLocalDateTime(),
    id_estado_servicio: 1  // ✅ NÚMERO en lugar de string
  });
  
  const [data, setData] = useState({
    clientes: [],
    tipos: [],
    periodos: [],
    locales: [],
    detalles: [],
    trabajadoresAsignados: [],
    tiposServicio: []  // 🆕 Tipos de servicio con configuracion de direcciones
  });

  // 🆕 Estados para direcciones
  const [tipoSeleccionado, setTipoSeleccionado] = useState(null);
  const [direccionOrigen, setDireccionOrigen] = useState({});
  const [destinos, setDestinos] = useState([]);
  const [guardarDirCliente, setGuardarDirCliente] = useState(false);

  const [dialogos, setDialogos] = useState({
    trabajadores: false,
    servicios: false,
    nuevoCliente: false
  });

  const [nuevoCliente, setNuevoCliente] = useState({
    nombre_cliente: '',
    apellido_cliente: '',
    tipo_documento: 'DNI',
    numero_documento: '',
    direccion_cliente: '',
    latitud_cliente: null,
    longitud_cliente: null,
    telefono_cliente: '',
    email_cliente: ''
  });

  const [error, setError] = useState('');
  const [errorCliente, setErrorCliente] = useState('');
  const [successCliente, setSuccessCliente] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingCliente, setLoadingCliente] = useState(false);
  const [totalOrden, setTotalOrden] = useState(0);
  
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { simboloMoneda } = useEmpresa();

  // Escuchar cambios en tiempo real cuando el trabajador modifica desde la APP
  const { ordenModificada, ultimaActualizacion, resetear: resetearRealtime } = useOrdenRealtime(id, (data) => {
    // Auto-recargar datos cuando el trabajador cambia estado
    loadData();
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const pasos = ['Información', 'Servicios', 'Personal'];
  const [pasoActual, setPasoActual] = useState(0);
  const isEditing = Boolean(id);

  // ✅ FUNCIONES DE VALIDACIÓN CORREGIDAS
  const isEditable = () => {
    const estadoActual = parseInt(orden.id_estado_servicio);
    return !estadosNoEditables.includes(estadoActual);
  };

  const canAddModifyServices = () => {
    const estadoActual = parseInt(orden.id_estado_servicio);
    return estadosModificacionPermitida.includes(estadoActual);
  };

  const canAssignWorkers = () => {
    const estadoActual = parseInt(orden.id_estado_servicio);
    return estadosAsignacionPermitida.includes(estadoActual);
  };

  // Nombre del estado desde la base de datos (no hardcodeado)
  const getEstadoDescriptivo = () => {
    return orden.nombre_estado_servicio || orden.descripcion_estado || 'Desconocido';
  };

  // ✅ FUNCIÓN getEstadoColor MEJORADA
  const getEstadoColor = (estado) => {
    let estadoTexto = estado;
    if (typeof estado === 'number') {
      const estadosMap = {
        1: 'pendiente',
        2: 'asignado',
        3: 'en camino', 
        4: 'en proceso',
        5: 'terminado',
        6: 'en espera',
        7: 'cancelado'
      };
      estadoTexto = estadosMap[estado] || 'desconocido';
    }
    
    switch(estadoTexto?.toLowerCase()) {
      case 'pendiente': return 'warning';
      case 'asignado': return 'info';
      case 'en espera': return 'warning';
      case 'en camino': return 'info';
      case 'en proceso': return 'primary';
      case 'terminado': return 'success';
      case 'completado': return 'success';
      case 'cancelado': return 'error';
      case 'rechazado': return 'error';
      case 'pausado': return 'default';
      default: return 'default';
    }
  };

  // ✅ TODOS LOS useEffect JUNTOS AL PRINCIPIO
  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    const total = data.detalles.reduce((sum, detalle) => {
      return sum + (detalle.cantidad * detalle.precio);
    }, 0);
    setTotalOrden(total);
  }, [data.detalles]);

  // 🆕 Actualizar tipo seleccionado cuando cambia el tipo de servicio
  useEffect(() => {
    if (orden.id_tiposervicio && data.tiposServicio.length > 0) {
      const tipo = data.tiposServicio.find(t => t.id_tipo_servicio === orden.id_tiposervicio);
      setTipoSeleccionado(tipo);

      // Resetear destinos si el tipo no los permite
      if (!tipo?.permite_multiples_destinos) {
        setDestinos([]);
      }

      // 🆕 Cargar dirección del cliente automáticamente si el tipo requiere dirección
      if (tipo?.requiere_direccion && orden.id_cliente && !direccionOrigen.direccion) {
        const clienteSeleccionado = data.clientes.find(c => c.id_cliente === orden.id_cliente);
        if (clienteSeleccionado?.direccion_cliente) {
          setDireccionOrigen({
            direccion: clienteSeleccionado.direccion_cliente,
            latitud: clienteSeleccionado.latitud_cliente || null,
            longitud: clienteSeleccionado.longitud_cliente || null,
            referencia: '',
            nombre_contacto: `${clienteSeleccionado.nombre_cliente} ${clienteSeleccionado.apellido_cliente}`,
            telefono_contacto: clienteSeleccionado.telefono_cliente || ''
          });
        }
      }
    }
  }, [orden.id_tiposervicio, data.tiposServicio]);

  // ✅ LOAD DATA CORREGIDO
  const loadData = async () => {
    setLoading(true);
    try {
      const [clientesRes, tiposRes, periodosRes, localesRes, tiposServicioRes] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/api/clientes`, {
          headers: { Authorization: `Bearer ${user.token}` }
        }),
        axios.get(`${config.API_BASE_URL}/api/general/tiposervicio`, {
          headers: { Authorization: `Bearer ${user.token}` }
        }),
        axios.get(`${config.API_BASE_URL}/api/periodos`, {
          headers: { Authorization: `Bearer ${user.token}` }
        }),
        axios.get(`${config.API_BASE_URL}/api/locales`, {
          headers: { Authorization: `Bearer ${user.token}` }
        }),
        // 🆕 Cargar tipos de servicio con configuracion de direcciones
        axios.get(`${config.API_BASE_URL}/api/v1/tipos-servicio`, {
          headers: { Authorization: `Bearer ${user.token}` }
        }).catch(() => ({ data: { data: [] } })) // Si falla, devolver array vacio
      ]);

      setData(prev => ({
        ...prev,
        clientes: clientesRes.data,
        tipos: tiposRes.data,
        periodos: periodosRes.data,
        locales: localesRes.data,
        tiposServicio: tiposServicioRes.data?.data || tiposServicioRes.data || []
      }));

      // Pre-cargar período activo y local (default o único) para órdenes nuevas
      if (!id) {
        const periodoActivo = periodosRes.data.find(p => p.contable === true);
        const locales = localesRes.data;
        const localDefault = locales.find(l => l.es_default) || (locales.length === 1 ? locales[0] : null);
        setOrden(prev => ({
          ...prev,
          id_periodo: periodoActivo ? periodoActivo.id_periodo : prev.id_periodo,
          id_local: localDefault ? localDefault.id_local : prev.id_local,
        }));

        // Auto-llenar dirección de origen con el local default (para delivery/express)
        if (localDefault?.latitud && localDefault?.longitud) {
          setDireccionOrigen({
            direccion: localDefault.direccion_local || '',
            latitud: localDefault.latitud,
            longitud: localDefault.longitud,
            referencia: '',
            nombre_contacto: '',
            telefono_contacto: localDefault.telefono_local || '',
          });
        }
      }

      if (id) {
        const [ordenRes, detallesRes, trabajadoresRes, destinosRes] = await Promise.all([
          axios.get(`${config.API_BASE_URL}/api/ordenes/${id}`, {
            headers: { Authorization: `Bearer ${user.token}` }
          }),
          axios.get(`${config.API_BASE_URL}/api/detalle-ordenes/${id}`, {
            headers: { Authorization: `Bearer ${user.token}` }
          }),
          axios.get(`${config.API_BASE_URL}/api/ordenes/${id}/trabajadores`, {
            headers: { Authorization: `Bearer ${user.token}` }
          }),
          // 🆕 Cargar destinos de la orden
          axios.get(`${config.API_BASE_URL}/api/v1/ordenes/${id}/destinos`, {
            headers: { Authorization: `Bearer ${user.token}` }
          }).catch(() => ({ data: { data: [] } }))
        ]);

        const ordenData = ordenRes.data;

        // ✅ ASEGURAR QUE EL ESTADO SEA NÚMERO
        setOrden({
          ...ordenData,
          fecha_orden: formatLocalDateTime(new Date(ordenData.fecha_orden)),
          id_estado_servicio: parseInt(ordenData.id_estado_servicio) // ✅ CONVERTIR A NÚMERO
        });

        // 🆕 Cargar direccion de origen
        if (ordenData.direccion_origen) {
          setDireccionOrigen({
            direccion: ordenData.direccion_origen,
            referencia: ordenData.referencia_origen,
            nombre_contacto: ordenData.contacto_origen,
            telefono_contacto: ordenData.telefono_origen,
            latitud: ordenData.latitud_origen,
            longitud: ordenData.longitud_origen
          });
        }

        // 🆕 Cargar destinos
        const destinosData = destinosRes.data?.data || destinosRes.data || [];
        setDestinos(destinosData);

        setData(prev => ({
          ...prev,
          detalles: detallesRes.data,
          trabajadoresAsignados: trabajadoresRes.data
        }));
      }
    } catch (error) {
      setError('Error al cargar los datos necesarios');
      console.error('Error:', error);
    } finally {
      setLoading(false);
      // Si es edición y estamos en paso 0 (primera carga), ir a paso 1
      // Si ya estamos en paso 2 o 3, NO resetear (evitar que vuelva atrás al recargar datos)
      if (id && pasoActual === 0) setPasoActual(1);
    }
  };

  const handleChange = (e) => {
    if (!isEditable()) return;
    const { name, value } = e.target;
    setOrden(prev => ({ ...prev, [name]: value }));

    // Si cambia el local y es tipo que requiere dirección, auto-llenar origen con dirección del local
    if (name === 'id_local' && tipoSeleccionado?.requiere_direccion) {
      const localSel = data.locales.find(l => l.id_local === parseInt(value));
      if (localSel?.latitud && localSel?.longitud) {
        setDireccionOrigen({
          direccion: localSel.direccion_local || '',
          latitud: localSel.latitud,
          longitud: localSel.longitud,
          referencia: '',
          nombre_contacto: '',
          telefono_contacto: localSel.telefono_local || '',
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditable()) return;

    setLoading(true);
    try {
      // 🆕 Preparar datos con direcciones
      const ordenData = {
        ...orden,
        // Direccion origen
        direccion_origen: direccionOrigen.direccion,
        latitud_origen: direccionOrigen.latitud,
        longitud_origen: direccionOrigen.longitud,
        referencia_origen: direccionOrigen.referencia,
        contacto_origen: direccionOrigen.nombre_contacto,
        telefono_origen: direccionOrigen.telefono_contacto,
        // Destinos
        destinos: destinos
      };

      // Si marcó guardar dirección en cliente, actualizar cliente
      if (guardarDirCliente && orden.id_cliente && destinos[0]) {
        try {
          await axios.put(
            `${config.API_BASE_URL}/api/clientes/${orden.id_cliente}`,
            {
              direccion_cliente: destinos[0].direccion,
              latitud: destinos[0].latitud,
              longitud: destinos[0].longitud,
            },
            { headers: { Authorization: `Bearer ${user.token}` } }
          );
        } catch (e) { console.error('Error guardando dirección en cliente:', e); }
      }

      if (id) {
        await axios.put(
          `${config.API_BASE_URL}/api/ordenes/${id}`,
          ordenData,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        setSuccess(true);
        await loadData();
        // Solo avanzar a paso 1 si estamos en paso 0 (info básica)
        // Si guardamos desde paso 2 o 3, quedarse ahí
        if (pasoActual === 0) setPasoActual(1);
      } else {
        const response = await axios.post(
          `${config.API_BASE_URL}/api/ordenes`,
          ordenData,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        setSuccess(true);
        if (tipoSeleccionado?.permite_multiples_destinos) {
          setTimeout(() => navigate(`/home/delivery/${response.data.id_orden_servicio}`), 1000);
        } else {
          // Redirigir a edición y avanzar al paso 2 (Servicios)
          setTimeout(() => {
            navigate(`/home/ordenes/editar/${response.data.id_orden_servicio}`);
          }, 800);
        }
      }
    } catch (error) {
      setError('Error al guardar la orden');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleCancel = () => {
    navigate('/home/ordenes');
  };

  // Funciones para abrir diálogos de gestión
  const abrirGestionTrabajadores = () => {
    setDialogos({ ...dialogos, trabajadores: true });
  };

  const cerrarGestionTrabajadores = () => {
    setDialogos({ ...dialogos, trabajadores: false });
  };

  const abrirGestionServicios = () => {
    setDialogos({ ...dialogos, servicios: true });
  };

  const cerrarGestionServicios = () => {
    setDialogos({ ...dialogos, servicios: false });
  };

  const recargarDatos = () => {
    loadData();
  };

  // Funciones para manejar creación rápida de clientes
  const abrirDialogoNuevoCliente = () => {
    setDialogos({ ...dialogos, nuevoCliente: true });
  };

  const cerrarDialogoNuevoCliente = () => {
    setDialogos({ ...dialogos, nuevoCliente: false });
    setNuevoCliente({
      nombre_cliente: '',
      apellido_cliente: '',
      tipo_documento: 'DNI',
      numero_documento: '',
      direccion_cliente: '',
      telefono_cliente: '',
      email_cliente: ''
    });
    setErrorCliente('');
    setSuccessCliente(false);
  };

  const handleChangeNuevoCliente = (e) => {
    const { name, value } = e.target;
    setNuevoCliente(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCrearCliente = async () => {
    // Validar campos obligatorios
    if (!nuevoCliente.nombre_cliente || !nuevoCliente.apellido_cliente ||
        !nuevoCliente.numero_documento || !nuevoCliente.telefono_cliente) {
      setErrorCliente('Por favor complete todos los campos obligatorios');
      return;
    }

    setLoadingCliente(true);
    setErrorCliente('');

    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/api/clientes`,
        nuevoCliente,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      // Mostrar mensaje de éxito
      setSuccessCliente(true);

      // Actualizar la lista de clientes
      const clientesRes = await axios.get(`${config.API_BASE_URL}/api/clientes`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setData(prev => ({ ...prev, clientes: clientesRes.data }));

      // Seleccionar el nuevo cliente automáticamente
      setOrden(prev => ({ ...prev, id_cliente: response.data.id_cliente }));

      // Si el cliente tiene ubicación y el servicio requiere dirección, pre-cargar
      if (tipoSeleccionado?.requiere_direccion && nuevoCliente.direccion_cliente) {
        setDireccionOrigen({
          direccion: nuevoCliente.direccion_cliente,
          latitud: nuevoCliente.latitud_cliente || null,
          longitud: nuevoCliente.longitud_cliente || null,
          referencia: '',
          nombre_contacto: `${nuevoCliente.nombre_cliente} ${nuevoCliente.apellido_cliente}`,
          telefono_contacto: nuevoCliente.telefono_cliente || ''
        });
      }

      // Cerrar el diálogo después de un breve delay
      setTimeout(() => {
        cerrarDialogoNuevoCliente();
      }, 1500);

    } catch (error) {
      setErrorCliente(error.response?.data?.error || 'Error al crear el cliente');
    } finally {
      setLoadingCliente(false);
    }
  };

  if (loading && isEditing) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3
      }}>
        <CircularProgress size={60} sx={{ mb: 2, color: '#1A237E' }} />
        <Typography variant="h6" color="text.secondary">
          Cargando datos de la orden...
        </Typography>
      </Box>
    );
  }

  // Encontrar el responsable
  const responsable = data.trabajadoresAsignados.find(t => t.es_responsable);

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100%',
      p: { xs: 2, sm: 3, md: 4 }
    }}>
      {/* Header con botón volver */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 4
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            sx={{
              color: '#1A237E',
              '&:hover': {
                backgroundColor: 'rgba(26, 35, 126, 0.04)'
              }
            }}
          >
            Volver
          </Button>
          
          <Box>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1"
              sx={{ 
                fontWeight: 'bold',
                color: '#1A237E',
                mb: 0.5
              }}
            >
              {isEditing ? `Editar Orden #${id}` : 'Nueva Orden de Servicio'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditing ? 'Gestión avanzada - Solo estados Pendiente y Asignado permiten modificaciones' : 'Crea una nueva orden de servicio'}
            </Typography>
          </Box>
        </Box>

        {/* Banner de actualización en tiempo real */}
        {ordenModificada && ultimaActualizacion && (
          <Alert
            severity="warning"
            sx={{ mb: 2, borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => { loadData(); resetearRealtime(); }}>
                Recargar
              </Button>
            }
          >
            Esta orden fue actualizada desde la APP móvil — Estado: <strong>{ultimaActualizacion.nombre_estado || 'Actualizado'}</strong>
          </Alert>
        )}

        {orden.id_estado_servicio && isEditing && (
          <Chip
            label={getEstadoDescriptivo()}
            color={getEstadoColor(getEstadoDescriptivo())}
            sx={{
              fontWeight: 'bold',
              fontSize: '0.875rem',
              height: 32
            }}
          />
        )}
      </Box>

      {/* Información del responsable */}
      {responsable && (
        <Paper sx={{ 
          mb: 3, 
          p: 3, 
          borderRadius: 3,
          border: '1px solid rgba(255, 193, 7, 0.3)',
          background: 'linear-gradient(135deg, #FFF8E1 0%, #FFFDE7 100%)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: '#FFB300', color: 'white' }}>
              <SupervisorIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#E65100' }}>
                👑 Responsable del Servicio
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {responsable.nombre_trabajador} {responsable.apellido_trabajador}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Solo esta persona puede cambiar los estados del servicio
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Alerts */}
      {!canAddModifyServices() && isEditing && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3, borderRadius: 2 }}
          icon={<WarningIcon />}
        >
          <strong>Modificaciones no permitidas</strong><br/>
          Solo se pueden modificar servicios y trabajadores en estados: <strong>Pendiente (1)</strong> y <strong>Asignado (2)</strong>.<br/>
          Estado actual: <strong>{getEstadoDescriptivo()} ({orden.id_estado_servicio})</strong>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          Orden {isEditing ? 'actualizada' : 'creada'} exitosamente.
        </Alert>
      )}

      {/* Stepper */}
      <Paper sx={{ mb: 2, p: { xs: 1.5, sm: 2 }, borderRadius: 3, border: '1px solid #E2E8F0' }}>
        <Stepper activeStep={pasoActual} alternativeLabel sx={{ '& .MuiStepConnector-line': { borderColor: '#E2E8F0' } }}>
          {pasos.map((label, index) => (
            <Step key={label}
              onClick={() => { if (id) setPasoActual(index); }}
              sx={{ cursor: id ? 'pointer' : 'default' }}
            >
              <StepLabel StepIconComponent={(props) =>
                <CustomStepIcon {...props} icon={index + 1} />
              }>
                <Typography variant="caption" fontWeight={700} color={pasoActual === index ? '#1A237E' : '#94A3B8'}>
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* ═══ PASO 1: Información Básica ═══ */}
      {pasoActual === 0 && (
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #E2E8F0', mb: 2 }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #1A237E, #3949AB)',
          color: '#fff', px: { xs: 2, sm: 3 }, py: 1.5,
          display: 'flex', alignItems: 'center', gap: 1
        }}>
          <AssignmentIcon sx={{ fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={700}>Información de la Orden</Typography>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>

              {/* Tipo de Servicio */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Servicio *</InputLabel>
                  <Select
                    name="id_tiposervicio"
                    value={orden.id_tiposervicio}
                    onChange={handleChange}
                    required
                    disabled={!isEditable()}
                    sx={{ borderRadius: 2 }}
                  >
                    {data.tiposServicio.map((tipo) => (
                      <MenuItem key={tipo.id_tipo_servicio} value={tipo.id_tipo_servicio}>
                        {tipo.nombre_tipo}
                        {tipo.requiere_direccion && ' (Requiere direccion)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Cliente — oculto en delivery (se define por destino) */}
              {!tipoSeleccionado?.permite_multiples_destinos && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Autocomplete
                    fullWidth
                    options={data.clientes}
                    getOptionLabel={(option) => `${option.nombre_cliente} ${option.apellido_cliente} - ${option.numero_documento || 'Sin documento'}`}
                    value={data.clientes.find(c => c.id_cliente === orden.id_cliente) || null}
                    onChange={(event, newValue) => {
                      setOrden(prev => ({
                        ...prev,
                        id_cliente: newValue ? newValue.id_cliente : ''
                      }));

                      // Cargar dirección del cliente si tiene dirección o coordenadas
                      if (newValue && tipoSeleccionado?.requiere_direccion &&
                          (newValue.direccion_cliente || newValue.latitud_cliente)) {
                        setDireccionOrigen({
                          direccion: newValue.direccion_cliente || '',
                          latitud: newValue.latitud_cliente || null,
                          longitud: newValue.longitud_cliente || null,
                          referencia: '',
                          nombre_contacto: `${newValue.nombre_cliente} ${newValue.apellido_cliente}`,
                          telefono_contacto: newValue.telefono_cliente || ''
                        });
                      }
                    }}
                    disabled={!isEditable()}
                    isOptionEqualToValue={(option, value) => option.id_cliente === value.id_cliente}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente *"
                        required
                        placeholder="Buscar por nombre, apellido o documento..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#4FC3F7' },
                            '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {option.nombre_cliente} {option.apellido_cliente}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.tipo_documento}: {option.numero_documento || 'Sin doc'}
                            {option.telefono_cliente && ` • ${option.telefono_cliente}`}
                            {option.direccion_cliente && ` • ${option.direccion_cliente.substring(0, 30)}...`}
                          </Typography>
                        </Box>
                        {option.latitud_cliente && (
                          <Tooltip title="Tiene ubicación GPS">
                            <LocationIcon sx={{ fontSize: 16, color: '#10B981', ml: 1 }} />
                          </Tooltip>
                        )}
                      </Box>
                    )}
                    noOptionsText="No se encontraron clientes"
                    sx={{
                      '& .MuiAutocomplete-clearIndicator': {
                        color: '#1A237E'
                      }
                    }}
                  />
                  <Tooltip title="Crear nuevo cliente">
                    <span>
                    <Button
                      variant="contained"
                      onClick={abrirDialogoNuevoCliente}
                      disabled={!isEditable()}
                      sx={{
                        minWidth: 'auto',
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #4FC3F7 0%, #29B6F6 100%)',
                        boxShadow: '0 4px 12px rgba(79, 195, 247, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #29B6F6 0%, #0288D1 100%)',
                          boxShadow: '0 6px 16px rgba(79, 195, 247, 0.4)',
                          transform: 'translateY(-2px)'
                        },
                        '&:disabled': {
                          background: '#E0E0E0'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <PersonAddIcon />
                    </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
              )}

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Local</InputLabel>
                  <Select
                    name="id_local"
                    value={orden.id_local}
                    onChange={handleChange}
                    required
                    disabled={!isEditable()}
                    sx={{ borderRadius: 2 }}
                  >
                    {data.locales.map((local) => (
                      <MenuItem key={local.id_local} value={local.id_local}>
                        {local.nombre_local}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Periodo</InputLabel>
                  <Select
                    name="id_periodo"
                    value={orden.id_periodo}
                    onChange={handleChange}
                    required
                    disabled={!isEditable()}
                    sx={{ borderRadius: 2 }}
                  >
                    {data.periodos.map((periodo) => (
                      <MenuItem key={periodo.id_periodo} value={periodo.id_periodo}>
                        {`${periodo.mes}/${periodo.anio}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Fecha y Hora del Servicio"
                  type="datetime-local"
                  name="fecha_orden"
                  value={orden.fecha_orden}
                  onChange={handleChange}
                  disabled={!isEditable()}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#4FC3F7' },
                      '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                    }
                  }}
                />
              </Grid>

              {/* 🆕 Seccion de Direcciones - Renderizado condicional segun tipo */}
              {tipoSeleccionado && !tipoSeleccionado.permite_multiples_destinos && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" sx={{ color: '#1A237E', fontWeight: 'bold', mb: 2 }}>
                      <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Direcciones del Servicio
                    </Typography>

                    {/* Direccion Origen - Si requiere_direccion */}
                    {tipoSeleccionado.requiere_direccion && (
                      <DireccionInput
                        label="Direccion de Origen / Cliente"
                        value={direccionOrigen}
                        onChange={setDireccionOrigen}
                        requiereCoordenadas={tipoSeleccionado.requiere_coordenadas}
                        disabled={!isEditable()}
                      />
                    )}

                    {/* Destino unico - Si requiere_destino pero NO multiples */}
                    {tipoSeleccionado.requiere_destino && !tipoSeleccionado.permite_multiples_destinos && (
                      <DireccionInput
                        label="Direccion de Destino"
                        value={destinos[0] || {}}
                        onChange={(data) => setDestinos([data])}
                        requiereCoordenadas={tipoSeleccionado.requiere_coordenadas}
                        disabled={!isEditable()}
                        showGuardarEnCliente={!!orden.id_cliente}
                        guardarEnCliente={guardarDirCliente}
                        onGuardarEnClienteChange={setGuardarDirCliente}
                      />
                    )}

                    {/* Multiples destinos */}
                    {tipoSeleccionado.permite_multiples_destinos && (
                      <DestinosMultiples
                        destinos={destinos}
                        onChange={setDestinos}
                        maxDestinos={tipoSeleccionado.max_destinos}
                        disabled={!isEditable()}
                        idOrden={id || null}
                        clientesExternos={data.clientes}
                      />
                    )}

                    {/* Mensaje si no requiere direcciones */}
                    {!tipoSeleccionado.requiere_direccion && (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Este tipo de servicio no requiere direccion (servicio fijo en local)
                      </Alert>
                    )}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2, 
                  justifyContent: 'flex-end',
                  mt: 3,
                  pt: 3,
                  borderTop: '1px solid rgba(26, 35, 126, 0.1)'
                }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    sx={{
                      borderColor: '#1A237E',
                      color: '#1A237E',
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      '&:hover': {
                        borderColor: '#0D47A1',
                        backgroundColor: 'rgba(26, 35, 126, 0.04)'
                      }
                    }}
                  >
                    Cancelar
                  </Button>
                  
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    disabled={!isEditable() || loading}
                    sx={{
                      background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 15px rgba(26, 35, 126, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                        boxShadow: '0 6px 20px rgba(26, 35, 126, 0.4)',
                        transform: 'translateY(-2px)'
                      },
                      '&:disabled': {
                        background: '#E0E0E0',
                        color: '#999'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {loading ? 'Guardando...' : (id ? 'Guardar y Continuar' : 'Crear Orden y Continuar')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Paper>
      )}

      {/* ═══ PASO 2: Servicios ═══ */}
      {pasoActual === 1 && id && (
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #E2E8F0', mb: 2 }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #10B981, #059669)',
            color: '#fff', px: { xs: 2, sm: 3 }, py: 1.5,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' }, gap: 1,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BuildIcon sx={{ fontSize: 20 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Servicios ({data.detalles.length})
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Total: {simboloMoneda} {totalOrden.toFixed(2)}
                </Typography>
              </Box>
            </Box>
            <Button size="small" variant="contained" startIcon={<EditIcon />}
              onClick={abrirGestionServicios} disabled={!canAddModifyServices()}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                textTransform: 'none', fontWeight: 700, fontSize: 13 }}>
              Gestionar Servicios
            </Button>
          </Box>

          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            {data.detalles.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: 'rgba(26, 35, 126, 0.04)' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Servicio</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Cantidad</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Precio Unit.</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.detalles.map((detalle) => (
                      <TableRow key={detalle.id_detalle} sx={{ '&:hover': { bgcolor: 'rgba(79, 195, 247, 0.08)' } }}>
                        <TableCell>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {detalle.nombre_servicio}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{detalle.cantidad}</TableCell>
                        <TableCell align="right">{simboloMoneda} {parseFloat(detalle.precio).toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {simboloMoneda} {(detalle.cantidad * detalle.precio).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'rgba(26, 35, 126, 0.04)' }}>
                      <TableCell colSpan={3} align="right">
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Total</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A237E' }}>
                          {simboloMoneda} {totalOrden.toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No hay servicios agregados. Usa "Gestionar Servicios" para agregar servicios.
              </Alert>
            )}

            {/* Navegación Paso 2 */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, pt: 2, borderTop: '1px solid #E2E8F0' }}>
              <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => setPasoActual(0)}
                sx={{ borderColor: '#1A237E', color: '#1A237E', borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}>
                Anterior
              </Button>
              <Button variant="contained" onClick={() => setPasoActual(2)}
                sx={{ background: 'linear-gradient(135deg, #1A237E, #3949AB)', borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}>
                Continuar a Personal
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* ═══ PASO 3: Personal ═══ */}
      {pasoActual === 2 && id && (
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #E2E8F0' }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #F59E0B, #D97706)',
            color: '#fff', px: { xs: 2, sm: 3 }, py: 1.5,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' }, gap: 1,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupIcon sx={{ fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={700}>
                Personal ({data.trabajadoresAsignados.length})
              </Typography>
            </Box>
            <Button size="small" variant="contained" startIcon={<GroupIcon />}
              onClick={abrirGestionTrabajadores} disabled={!canAssignWorkers()}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                textTransform: 'none', fontWeight: 700, fontSize: 13 }}>
              Gestionar Personal
            </Button>
          </Box>

          <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            {data.trabajadoresAsignados.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {data.trabajadoresAsignados.map((trabajador) => {
                  const precio = parseFloat(trabajador.precio_servicio || 0);
                  const comisionPct = parseFloat(trabajador.comision || 0);
                  const comisionFlat = parseFloat(trabajador.comision_flat || 0);
                  const comisionMonto = comisionFlat > 0 ? comisionFlat : (precio * comisionPct / 100);

                  return (
                    <Box key={trabajador.id_asignacion} sx={{
                      border: trabajador.es_responsable ? '2px solid #F59E0B' : '1px solid #E2E8F0',
                      borderRadius: 2.5, p: { xs: 1.5, sm: 2 }, position: 'relative',
                      bgcolor: trabajador.es_responsable ? '#FFFBEB' : '#fff',
                      transition: 'all 0.2s', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
                    }}>
                      {/* Header: Avatar + Nombre + Chips */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Avatar sx={{ bgcolor: '#1A237E', width: 36, height: 36, fontSize: 14, fontWeight: 700 }}>
                          {trabajador.nombre_trabajador?.[0]}{trabajador.apellido_trabajador?.[0]}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {trabajador.nombre_trabajador} {trabajador.apellido_trabajador}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {trabajador.es_responsable && (
                              <Chip label="Responsable" size="small" icon={<StarIcon sx={{ fontSize: 12 }} />}
                                sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: '#F59E0B', color: '#fff',
                                  '& .MuiChip-icon': { color: '#fff' } }} />
                            )}
                            <Chip label={trabajador.descripcion_estado} size="small"
                              color={getEstadoColor(trabajador.descripcion_estado)}
                              sx={{ height: 20, fontSize: 10 }} />
                          </Box>
                        </Box>
                      </Box>

                      {/* Comisión: 3 columnas compactas */}
                      <Box sx={{
                        display: 'flex', gap: 1, bgcolor: '#F8FAFC', borderRadius: 2, p: 1,
                        border: '1px solid #E2E8F0',
                      }}>
                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary" display="block">Precio</Typography>
                          <Typography variant="body2" fontWeight={700} color="#1A237E">
                            {simboloMoneda} {precio.toFixed(2)}
                          </Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary" display="block">Comisión</Typography>
                          <Typography variant="body2" fontWeight={700} color="#D97706">
                            {comisionFlat > 0 ? `${simboloMoneda} ${comisionFlat.toFixed(2)}` : `${comisionPct}%`}
                          </Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary" display="block">Gana</Typography>
                          <Typography variant="body2" fontWeight={800} color="#10B981">
                            {simboloMoneda} {comisionMonto.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No hay personal asignado. Usa "Gestionar Personal" para asignar trabajadores.
              </Alert>
            )}

            {/* Navegación Paso 3 */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 1.5, borderTop: '1px solid #E2E8F0' }}>
              <Button size="small" variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => setPasoActual(1)}
                sx={{ borderColor: '#1A237E', color: '#1A237E', borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
                Anterior
              </Button>
              <Button size="small" variant="contained" startIcon={<CheckCircleIcon />} onClick={() => navigate('/home/ordenes')}
                sx={{ background: 'linear-gradient(135deg, #10B981, #059669)', borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
                Finalizar
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Campos Dinámicos del Estado */}
      {id && orden.id_estado_servicio && (
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <CamposDinamicosRenderer
            idOrden={id}
            idEstado={orden.id_estado_servicio}
            soloLectura={!isEditable()}
            onValoresChange={(valores) => {
              console.log('Valores campos dinámicos:', valores);
            }}
          />
        </Paper>
      )}

      {/* Diálogos de gestión */}
      <GestionTrabajadoresDialog
        open={dialogos.trabajadores}
        onClose={cerrarGestionTrabajadores}
        ordenId={id}
        ordenEstado={orden.id_estado_servicio}
        trabajadoresAsignados={data.trabajadoresAsignados}
        serviciosActuales={data.detalles} 
        onTrabajadoresUpdated={recargarDatos}
      />

      <GestionServiciosDialog
        open={dialogos.servicios}
        onClose={cerrarGestionServicios}
        ordenId={id}
        ordenEstado={orden.id_estado_servicio}
        serviciosActuales={data.detalles}
        onServiciosUpdated={recargarDatos}
      />

      {/* Botones de navegación finales */}
      <Box sx={{ mt: 4, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/home/ordenes')}
          sx={{
            borderColor: '#1A237E',
            color: '#1A237E',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 'bold'
          }}
        >
          Volver a la lista
        </Button>
        
        {id && !canAddModifyServices() && (
          <Alert severity="info" sx={{ flex: 1, ml: 2, borderRadius: 2 }}>
            Solo se pueden hacer modificaciones en estados <strong>Pendiente</strong> y <strong>Asignado</strong>
          </Alert>
        )}
      </Box>

      {/* Diálogo para Crear Nuevo Cliente */}
      {/* Dialog Nuevo Cliente — fullscreen en mobile, con mapa */}
      <Dialog
        open={dialogos.nuevoCliente}
        onClose={cerrarDialogoNuevoCliente}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, maxHeight: isMobile ? '100%' : '92vh' } }}
      >
        {/* Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
          color: '#fff', px: 2.5, py: 2,
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <PersonAddIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 800 }}>Nuevo Cliente</Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
              Ubícalo en el mapa para servicios más inteligentes
            </Typography>
          </Box>
          <IconButton onClick={cerrarDialogoNuevoCliente} sx={{ color: '#fff' }}>
            <CancelIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 2 }}>
            {errorCliente && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setErrorCliente('')}>
                {errorCliente}
              </Alert>
            )}
            {successCliente && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                Cliente creado exitosamente
              </Alert>
            )}

            {/* Datos personales */}
            <Box sx={{
              border: '1px solid #E2E8F0', borderRadius: 3, p: 2, mb: 2, bgcolor: '#FAFBFC',
            }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1A237E', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Datos personales
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <TextField fullWidth label="Nombre *" size="small" name="nombre_cliente"
                    value={nuevoCliente.nombre_cliente} onChange={handleChangeNuevoCliente}
                    sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Apellido *" size="small" name="apellido_cliente"
                    value={nuevoCliente.apellido_cliente} onChange={handleChangeNuevoCliente}
                    sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Doc</InputLabel>
                    <Select name="tipo_documento" label="Doc" value={nuevoCliente.tipo_documento}
                      onChange={handleChangeNuevoCliente} sx={{ borderRadius: 2 }}>
                      <MenuItem value="DNI">DNI</MenuItem>
                      <MenuItem value="RUC">RUC</MenuItem>
                      <MenuItem value="CE">CE</MenuItem>
                      <MenuItem value="Pasaporte">Pasaporte</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={8}>
                  <TextField fullWidth label="Nro Documento *" size="small" name="numero_documento"
                    value={nuevoCliente.numero_documento} onChange={handleChangeNuevoCliente}
                    sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                </Grid>
              </Grid>
            </Box>

            {/* Contacto */}
            <Box sx={{
              border: '1px solid #E2E8F0', borderRadius: 3, p: 2, mb: 2, bgcolor: '#FAFBFC',
            }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1A237E', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Contacto
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <TextField fullWidth label="Teléfono *" size="small" name="telefono_cliente"
                    value={nuevoCliente.telefono_cliente} onChange={handleChangeNuevoCliente}
                    sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Email" size="small" name="email_cliente" type="email"
                    value={nuevoCliente.email_cliente} onChange={handleChangeNuevoCliente}
                    sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }} />
                </Grid>
              </Grid>
            </Box>

            {/* Ubicación con DireccionInput + Mapa */}
            <DireccionInput
              label="Ubicación del cliente (opcional)"
              value={{
                direccion: nuevoCliente.direccion_cliente || '',
                latitud: nuevoCliente.latitud_cliente,
                longitud: nuevoCliente.longitud_cliente,
              }}
              onChange={(val) => setNuevoCliente(prev => ({
                ...prev,
                direccion_cliente: val.direccion || '',
                latitud_cliente: val.latitud || null,
                longitud_cliente: val.longitud || null,
              }))}
              showContacto={false}
              showMap={true}
            />

            {/* Hint */}
            {nuevoCliente.latitud_cliente && nuevoCliente.longitud_cliente && (
              <Alert severity="info" sx={{ borderRadius: 2, mt: -1 }} icon={false}>
                <Typography sx={{ fontSize: 12 }}>
                  Con la ubicación del cliente, el sistema puede pre-cargar direcciones en servicios móviles y calcular rutas.
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>

        {/* Footer */}
        <Box sx={{ borderTop: '1px solid #E2E8F0', p: 2, display: 'flex', gap: 1.5 }}>
          <Box onClick={cerrarDialogoNuevoCliente}
            sx={{
              flex: 1, py: 1.2, borderRadius: 2.5, textAlign: 'center',
              border: '1.5px solid #E2E8F0', cursor: 'pointer',
              fontSize: 14, fontWeight: 600, color: '#64748B',
              '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' },
            }}>
            Cancelar
          </Box>
          <Box
            onClick={loadingCliente || !nuevoCliente.nombre_cliente || !nuevoCliente.apellido_cliente ||
              !nuevoCliente.numero_documento || !nuevoCliente.telefono_cliente ? undefined : handleCrearCliente}
            sx={{
              flex: 1.5, py: 1.2, borderRadius: 2.5, textAlign: 'center',
              background: !nuevoCliente.nombre_cliente || !nuevoCliente.apellido_cliente ||
                !nuevoCliente.numero_documento || !nuevoCliente.telefono_cliente
                ? '#CBD5E1' : 'linear-gradient(135deg, #10B981, #059669)',
              cursor: !nuevoCliente.nombre_cliente || !nuevoCliente.telefono_cliente ? 'default' : 'pointer',
              fontSize: 14, fontWeight: 700, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
              '&:hover': { background: !nuevoCliente.nombre_cliente ? '#CBD5E1' : 'linear-gradient(135deg, #059669, #047857)' },
            }}>
            {loadingCliente ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
            {loadingCliente ? 'Creando...' : 'Crear Cliente'}
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}

export default OrdenServicioForm;